import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GridRowParams, GridPaginationModel } from "@mui/x-data-grid";
import { Org } from "~/lib/types";
import {
  useLoadOrganizationUsersQuery,
  MemberIdentityType,
} from "~/operations";
import { UserRow } from "../types";
import { sortRolesBySeniority, stripOrgPrefix } from "../lib";

const DEFAULT_PAGE_SIZE = 25;

export function useUsersList(org: Org, rolesFilter?: string[], query?: string) {
  const navigate = useNavigate();
  const [addMemberOpen, setAddMemberOpen] = useState(false);

  // Pagination state
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Cache cursors for each page
  const [cursorCache, setCursorCache] = useState<Map<number, string>>(
    new Map(),
  );

  // Calculate after cursor for current page
  const after =
    paginationModel.page > 0
      ? cursorCache.get(paginationModel.page - 1)
      : undefined;

  // TODO roles filter will not work while x-mondoo-feature-flags: org_members=enabled is applied
  const { data, loading } = useLoadOrganizationUsersQuery({
    variables: {
      mrn: org.mrn,
      first: paginationModel.pageSize,
      after,
      query: query || undefined,
      identityTypeFilter: [MemberIdentityType.User],
      rolesFilter: rolesFilter || undefined,
    },
  });

  const members = data?.organization?.members?.edges || [];
  const pageInfo = data?.organization?.members?.pageInfo;

  // Memoize rowCount to prevent MUI DataGrid from resetting pagination during loading
  const rowCountRef = useRef(0);
  const rowCount = useMemo(() => {
    const newCount = data?.organization?.members?.totalCount;
    if (newCount !== undefined) {
      rowCountRef.current = newCount;
    }
    return rowCountRef.current;
  }, [data?.organization?.members?.totalCount]);

  const rows: UserRow[] = members.map((edge) => {
    const node = edge?.node;
    const identity = node?.identity;
    return {
      id: identity?.mrn || "",
      mrn: identity?.mrn || "",
      name: identity?.name || "",
      email: (identity as { email?: string })?.email || "",
      scope: "—", // Placeholder - API doesn't provide this yet
      lastActive: null, // Placeholder - API doesn't provide this yet
      roles: sortRolesBySeniority(
        (node?.roles || [])
          .filter((r) => r !== null)
          .map((r) => ({ mrn: r.mrn, title: stripOrgPrefix(r.title) })),
      ),
    };
  });

  // Cache endCursor for current page when data arrives
  useEffect(() => {
    if (pageInfo?.endCursor) {
      setCursorCache((prev) => {
        const next = new Map(prev);
        next.set(paginationModel.page, pageInfo.endCursor);
        return next;
      });
    }
  }, [pageInfo?.endCursor, paginationModel.page]);

  // Reset cursor cache and go to first page when query or roles filter changes
  useEffect(() => {
    setCursorCache(new Map());
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [query, JSON.stringify(rolesFilter)]);

  const handleRowClick = (params: GridRowParams<UserRow>) => {
    const encodedMrn = encodeURIComponent(params.row.mrn);
    navigate(`/organization/users/${encodedMrn}?organizationId=${org.id}`);
  };

  // Handle pagination model changes
  const handlePaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      // If page size changed, reset to first page and clear cache
      if (newModel.pageSize !== paginationModel.pageSize) {
        setCursorCache(new Map());
        setPaginationModel({ page: 0, pageSize: newModel.pageSize });
      } else {
        setPaginationModel(newModel);
      }
    },
    [paginationModel.pageSize],
  );

  const openAddMemberDialog = () => setAddMemberOpen(true);
  const closeAddMemberDialog = () => setAddMemberOpen(false);

  return {
    addMemberOpen,
    openAddMemberDialog,
    closeAddMemberDialog,
    loading,
    rows,
    handleRowClick,
    // Pagination
    paginationModel,
    onPaginationModelChange: handlePaginationModelChange,
    rowCount,
  };
}
